import React, { useEffect, useMemo, useState } from "react";
import { api, fmtEUR, fmtDate } from "@/lib/api";
import { PageHeader, StatusBadge, EmptyState } from "@/components/ui-parts";
import { Archive as ArchiveIcon, ExternalLink, Filter } from "lucide-react";
import { toast } from "sonner";

const sourceColors = {
  "eurostay": "bg-[#EAF4FA] text-[#1A4971]",
  "eurostay.world": "bg-[#EAF4FA] text-[#1A4971]",
  "mobigast": "bg-[#FFF4E5] text-[#915000]",
  "mobigast.de": "bg-[#FFF4E5] text-[#915000]",
  "mont-aro": "bg-[#E4F0E8] text-[#18322B]",
  "mont-aro.de": "bg-[#E4F0E8] text-[#18322B]",
};

const sourceBadge = (src) => sourceColors[src] || sourceColors[(src || "").toLowerCase()] || "bg-[#F3F3F1] text-[#5C5E60]";

export default function Archive() {
  const [items, setItems] = useState([]);
  const [sources, setSources] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filterSource, setFilterSource] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterSource) params.source_app = filterSource;
      if (filterCompany) params.company_id = filterCompany;
      const [i, s, c] = await Promise.all([
        api.get("/archive", { params }),
        api.get("/archive/sources"),
        api.get("/companies"),
      ]);
      setItems(i.data);
      setSources(s.data);
      setCompanies(c.data);
    } catch { toast.error("Konnte Archiv nicht laden"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterSource, filterCompany]);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const totals = useMemo(() => {
    const total = items.reduce((s, r) => s + (r.total_amount || 0), 0);
    const bySource = {};
    items.forEach((r) => {
      const k = r.source_app || "sonstige";
      if (!bySource[k]) bySource[k] = { count: 0, sum: 0 };
      bySource[k].count += 1;
      bySource[k].sum += r.total_amount || 0;
    });
    return { total, bySource };
  }, [items]);

  return (
    <div>
      <PageHeader
        title="Rechnungsarchiv"
        subtitle="Zentrales Archiv aller bezahlten Rechnungen aus Ihren angebundenen Apps."
      />

      <div className="px-8 py-8 space-y-6">
        {/* Source cards */}
        {sources.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={() => setFilterSource("")}
              className={`text-left bg-white border rounded-md p-5 transition-colors duration-150 ${filterSource === "" ? "border-[#18322B] bg-[#E4F0E8]" : "border-[#E6E6E4] hover:bg-[#F9F9F8]"}`}
              data-testid="source-all"
            >
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5E60] font-semibold">Gesamt</div>
              <div className="font-display font-extrabold text-2xl mt-1 tracking-tight">{items.length ? items.length : sources.reduce((a, b) => a + b.count, 0)}</div>
              <div className="text-xs text-[#5C5E60] mt-0.5 font-mono-tab">
                {fmtEUR(items.length ? totals.total : sources.reduce((a, b) => a + b.total, 0))}
              </div>
            </button>
            {sources.map((s) => (
              <button
                key={s.source_app}
                onClick={() => setFilterSource(s.source_app)}
                className={`text-left bg-white border rounded-md p-5 transition-colors duration-150 ${filterSource === s.source_app ? "border-[#18322B] bg-[#E4F0E8]" : "border-[#E6E6E4] hover:bg-[#F9F9F8]"}`}
                data-testid={`source-${s.source_app}`}
              >
                <div className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded inline-block ${sourceBadge(s.source_app)}`}>
                  {s.source_app || "sonstige"}
                </div>
                <div className="font-display font-extrabold text-2xl mt-2 tracking-tight">{s.count}</div>
                <div className="text-xs text-[#5C5E60] mt-0.5 font-mono-tab">{fmtEUR(s.total)}</div>
              </button>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3">
          <Filter className="h-4 w-4 text-[#5C5E60]" />
          <select
            value={filterCompany}
            onChange={(e) => setFilterCompany(e.target.value)}
            data-testid="archive-filter-company"
            className="px-3 py-2 border border-[#E6E6E4] rounded-md text-sm bg-white focus:outline-none focus:border-[#18322B]"
          >
            <option value="">Alle Firmen</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {(filterSource || filterCompany) && (
            <button onClick={() => { setFilterSource(""); setFilterCompany(""); }}
              className="text-xs text-[#18322B] hover:underline">Filter zurücksetzen</button>
          )}
        </div>

        {loading ? (
          <div className="text-sm text-[#5C5E60]">Lade...</div>
        ) : items.length === 0 ? (
          <EmptyState
            title="Noch keine archivierten Rechnungen"
            hint="Externe Apps können via API-Key an POST /api/external/invoices Rechnungen einsenden. Details unter API-Zugänge."
            testid="archive-empty"
          />
        ) : (
          <div className="bg-white border border-[#E6E6E4] rounded-md overflow-hidden">
            <table className="w-full text-sm" data-testid="archive-list">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.15em] text-[#5C5E60] bg-[#F9F9F8] border-b border-[#E6E6E4]">
                  <th className="text-left px-6 py-3 font-semibold">Quelle</th>
                  <th className="text-left px-4 py-3 font-semibold">Aussteller</th>
                  <th className="text-left px-4 py-3 font-semibold">Rechnungs-Nr.</th>
                  <th className="text-left px-4 py-3 font-semibold">Datum</th>
                  <th className="text-right px-4 py-3 font-semibold">Betrag</th>
                  <th className="text-left px-4 py-3 font-semibold">Bezahlt am</th>
                  <th className="text-left px-4 py-3 font-semibold">Zuordnung</th>
                  <th className="text-right px-6 py-3 font-semibold">Datei</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const c = companies.find((x) => x.id === r.company_id);
                  return (
                    <tr key={r.id} className="border-b border-[#E6E6E4] last:border-b-0 hover:bg-[#F9F9F8] transition-colors duration-150" data-testid={`archive-row-${r.id}`}>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-2">
                          <ArchiveIcon className="h-4 w-4 text-[#5C5E60]" strokeWidth={1.75} />
                          <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${sourceBadge(r.source_app)}`}>
                            {r.source_app}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-[#1A1A1A]">{r.issuing_company || r.vendor}</div>
                        {r.customer_reference && <div className="text-xs text-[#5C5E60]">an {r.customer_reference}</div>}
                      </td>
                      <td className="px-4 py-3 font-mono-tab text-xs text-[#5C5E60]">{r.invoice_number || "—"}</td>
                      <td className="px-4 py-3 font-mono-tab text-[#1A1A1A]">{fmtDate(r.date)}</td>
                      <td className="text-right px-4 py-3 font-mono-tab font-semibold">{fmtEUR(r.total_amount, r.currency)}</td>
                      <td className="px-4 py-3 font-mono-tab text-xs text-[#5C5E60]">
                        {r.paid_at ? fmtDate(r.paid_at) : "—"}
                        {r.payment_method && <div className="text-[10px]">{r.payment_method}</div>}
                      </td>
                      <td className="px-4 py-3">
                        {c ? (
                          <div className="flex items-center gap-1.5 text-xs">
                            <span className="h-2 w-2 rounded-full" style={{ background: c.color }} />
                            <span className="text-[#1A1A1A]">{c.name}</span>
                          </div>
                        ) : (
                          <StatusBadge variant="warning">Keine Firma</StatusBadge>
                        )}
                      </td>
                      <td className="text-right px-6 py-3">
                        <a href={`${backendUrl}/api/files/${r.storage_path}`} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md hover:bg-[#F3F3F1] text-[#1A1A1A] transition-colors duration-150"
                          data-testid={`archive-open-${r.id}`}>
                          <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} /> Öffnen
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
